import Phaser from 'phaser';
import type { PuppyId, Color } from '../game/types';
import { puppyForId } from '../game/Puppy';
import { pickIdleAnimKey } from './puppyAnimations';
import { PUPPY_ANCHORS, PUPPY_MAX_DIM } from './puppyAnchors.generated';
import { alignmentForStill, alignmentForAnim } from './puppyAlignmentOffsets';
import { Z } from './zOrder';

// Hex values sampled from iOS squaresbevel/color{N}@2x.png.
const COLOR_HEX: Record<Color, number> = {
  red: 0xea0714,
  yellow: 0xffe600,
  orange: 0xfd8900,
  blue: 0x0024ff,
  green: 0x03da18,
  pink: 0xe6498e,
  purple: 0x7203da,
};

// Mirrors Header.swift::headerDoUpdate's 4-second cadence for the random
// big-puppy animation.
const ANIM_INTERVAL_MS = 4000;

// The active-puppy preview shown in the header. Defaults to the static
// `puppy{N}@2x.png` face (matching iOS) and runs a random idle animation
// every ANIM_INTERVAL_MS. Each frame's per-frame nose anchor (computed by
// scripts/compute-puppy-anchors.py) is applied via setOrigin so the dog's
// nose stays pinned to the panel centre across still ↔ animation.
export class ColorPickerView {
  readonly container: Phaser.GameObjects.Container;
  private colorSquare: Phaser.GameObjects.Rectangle;
  private puppySprite: Phaser.GameObjects.Sprite;
  private staticSize: number;
  private currentPuppy: PuppyId | null = null;
  // null = still mode; integer = currently playing animation (0-based animIdx).
  private currentAnimIdx: number | null = null;
  private animTimer: Phaser.Time.TimerEvent;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, x: number, y: number, sizePx = 90) {
    this.scene = scene;
    this.container = scene.add.container(x, y).setDepth(Z.colorPicker);
    this.staticSize = sizePx * 0.85;

    this.colorSquare = scene.add
      .rectangle(0, 0, sizePx, sizePx, COLOR_HEX.red, 1)
      .setStrokeStyle(4, 0xffffff);
    // Default to the static large-puppy face (matches iOS default state).
    this.puppySprite = scene.add
      .sprite(0, 0, 'puppieslg', 'puppy1@2x.png')
      .setOrigin(0.5)
      .setDisplaySize(this.staticSize, this.staticSize);

    this.container.add([this.colorSquare, this.puppySprite]);

    // Schedule the periodic random animation.
    this.animTimer = scene.time.addEvent({
      delay: ANIM_INTERVAL_MS,
      loop: true,
      callback: () => this.playRandomAnim(),
    });
  }

  setActive(puppyId: PuppyId, color: Color): void {
    this.colorSquare.setFillStyle(COLOR_HEX[color] ?? 0xffffff, 1);
    this.currentPuppy = puppyId;
    this.resetToStatic(puppyId);
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  destroy(): void {
    this.animTimer.remove(false);
    this.container.destroy(true);
  }

  // Apply the per-frame nose anchor so the dog's nose lands at the sprite's
  // container-local (0, 0) — the centre of the panel — regardless of which
  // texture / frame is currently displayed. Uses a uniform per-puppy scale
  // (NOT setDisplaySize) so frames keep their natural aspect — tall/wide
  // anim poses don't get squashed, and puppies are free to extend outside
  // the panel as the artwork intended.
  private applyFrameAnchor(atlasKey: string, frameName: string, puppyNum: number): void {
    const a = PUPPY_ANCHORS[atlasKey]?.[frameName] ?? { x: 0.5, y: 0.5 };
    const maxDim = PUPPY_MAX_DIM[String(puppyNum)] ?? 200;
    const scale = this.staticSize / maxDim;
    this.puppySprite.setScale(scale);
    this.puppySprite.setOrigin(a.x, a.y);
    // Per-puppy tuning offset (puppyAlignmentOffsets.ts), scaled from the
    // reference display size down to this panel's size.
    const offset = this.currentPuppy
      ? this.currentAnimIdx === null
        ? alignmentForStill(this.currentPuppy, this.staticSize)
        : alignmentForAnim(this.currentPuppy, this.currentAnimIdx, this.staticSize)
      : { dx: 0, dy: 0 };
    this.puppySprite.setPosition(offset.dx, offset.dy);
  }

  private resetToStatic(puppyId: PuppyId): void {
    const num = puppyForId(puppyId).puppyNum;
    this.currentAnimIdx = null;
    this.puppySprite.anims.stop();
    this.puppySprite.off('animationupdate');
    this.puppySprite.off('animationcomplete');
    // Still frame = first frame of anim1. The static puppieslg face has
    // different padding than the anim frames, so using anim1.frame1 keeps
    // the still→anim transition seamless (it's literally the same image).
    const stillAtlas = `p${num}a1`;
    const stillFrame = 'frame1';
    if (this.scene.textures.exists(stillAtlas)) {
      this.puppySprite.setTexture(stillAtlas, stillFrame);
      this.applyFrameAnchor(stillAtlas, stillFrame, num);
    } else {
      this.puppySprite.setTexture('puppieslg', `puppy${num}@2x.png`);
      this.applyFrameAnchor('puppieslg', `puppy${num}@2x.png`, num);
    }
  }

  private playRandomAnim(): void {
    if (!this.currentPuppy) return;
    if (!this.container.visible) return;
    if (this.puppySprite.anims.isPlaying) return;

    const num = puppyForId(this.currentPuppy).puppyNum;
    const animKey = pickIdleAnimKey(num, (max) => Math.floor(Math.random() * max));
    const atlasKey = animKey.replace(/^puppy(\d+)\.anim(\d+)$/, 'p$1a$2');
    if (!this.scene.anims.exists(animKey)) return;
    const animNum = Number(animKey.match(/anim(\d+)$/)?.[1] ?? 1);
    this.currentAnimIdx = animNum - 1;

    // Update origin on every frame change so the nose stays put.
    this.puppySprite.off('animationupdate');
    this.puppySprite.on(
      'animationupdate',
      (_anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
        this.applyFrameAnchor(atlasKey, frame.textureFrame as string, num);
      },
    );

    this.puppySprite.play({ key: animKey, repeat: 0 });
    // Anchor the first frame immediately (animationupdate fires from frame 2).
    const firstFrame = this.scene.anims.get(animKey)?.frames[0]?.textureFrame as string | undefined;
    if (firstFrame) this.applyFrameAnchor(atlasKey, firstFrame, num);

    this.puppySprite.once('animationcomplete', () => {
      this.puppySprite.off('animationupdate');
      if (this.currentPuppy) this.resetToStatic(this.currentPuppy);
    });
  }
}
